/**
 * This class is ONLY used for QUEUES!
 */

import type {IndexableType, Table} from 'dexie';
import Dexie from 'dexie';
import ls from './localStorage';
import {EntityType} from "@/@types/entity";
import {getCurrentFirmId, getCurrentUserId} from "./picos/currentLogin";
import {Err, None, Ok, Option, Result, Some} from "oxide.ts";
import {cast} from "@/entities/schemas";
import {queueCountSlice} from "@/store/queueCount";

let logging = ls.getBoolean(ls.QUEUE_LOGGING, false);

// This is the IDB that we will be working with: Queues
let _db: Option<Dexie> = None;

let _isOldVersion = false;

/** Message for an unknown thrown value (handles non-Error throws). */
function errMsg(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

/**
 *
 * @param {string} [firmId]
 * @param {string} [userId]
 * @returns {Option<Dexie>}
 */
export function getDB(firmId?: string, userId?: string): Result<Dexie, Error> {
    // If it already exists, and it has a value, return it.
    if (_db.isSome()) return Ok(_db.unwrap());

    // Try to instantiate it
    if ((firmId || getCurrentFirmId()) && (userId || getCurrentUserId())) {
        const dbName = `Queues_${firmId || getCurrentFirmId()}_${userId || getCurrentUserId()}`;
        try {
            if (logging) console.debug(`⟴ IDB: getDB() creating new Dexie instance: "${dbName}"`);
            const db = new Dexie(dbName);
            _db = Some(db);
            return Ok(db);
        }
        catch (err) {
            return Err(new Error(`⟴ IDB: getDB() failed to create Dexie instance "${dbName}". ${errMsg(err)}`));
        }
    }
    else {
        try {
            if (logging) console.debug(`⟴ IDB: getDB() creating bare Dexie instance: "Queues" (no firmId/userId available)`);
            const db = new Dexie(`Queues`);
            _db = Some(db);
            return Ok(db);
        }
        catch (err) {
            return Err(new Error(`⟴ IDB: getDB() failed to create bare Dexie instance "Queues". ${errMsg(err)}`));
        }
    }
}

export function getOldDb(): Option<Dexie> {
    try {
        const db = new Dexie(`Queues`);
        _isOldVersion = true;
        _db = Some(db);
        return _db;
    }
    catch (err) {
        return None;
    }
}

const INITIAL_TABLES = ['user', 'firm', 'goal', 'task', 'contact'];
let tables: string[] = [];

export const VERSION = 38;

/**
 * Create a new version schema for the database.
 *
 * CAUTION: In order to make this function usable, we must assume that the database is available.
 * Thus calls to the method MUST CHECK that the database is open FIRST.  We will log an error if
 * it is not the case.
 */
function createVersion(version: number): number {
    // Only ever called from open(), which has already verified getDB() — the
    // instance is cached, so unwrap() cannot fail here.
    const db = getDB().unwrap();
    const stores: Record<string, string> = {};

    tables.forEach(e => {
        if (_isOldVersion) stores[e] = `_addedOn, ${e}Id`;
        else stores[e] = `addedOn, ${e}Id`;
    });

    db.version(++version).stores(stores);

    return version;
}

/**
 * Check if the database is open.
 */
export function isOpen(): Result<boolean, Error> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: isOpen() failed — no database instance. ${dbResult.unwrapErr().message}`));

    return Ok(dbResult.unwrap().isOpen());
}

/**
 * Close the database and sets the internal db to None.
 */
export function close(): Result<void, Error> {
    const dbResult = getDB();
    if (dbResult.isOk()) {
        const db = dbResult.unwrap();
        if (logging) console.debug(`⟴ IDB: close() — db="${db.name}", wasOpen=${db.isOpen()}`);
        if (db.isOpen()) db.close();
        _db = None;
        _isOldVersion = false;
        return Ok(null);
    }
    return Err(new Error(`⟴ IDB: close() failed — no database instance. ${dbResult.unwrapErr().message}`));
}

/**
 * Open the database.
 *
 * This function contains the schema for all versions of the database (can not automatically generate)
 *
 * @param {string} [userId] - if passed, force use
 * @param {string} [firmId] - if passed, force use
 * @returns {Dexie}
 */
export function open(firmId?: string, userId?: string): Result<Dexie, Error> {
    const dbResult = getDB(firmId, userId);
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: open(firmId=${firmId ?? 'undefined'}, userId=${userId ?? 'undefined'}) failed — could not get database instance. ${dbResult.unwrapErr().message}`));
    const db = dbResult.unwrap();

    // Reset tables to initial state so open() is safe to call multiple times
    tables = [...INITIAL_TABLES];

    // First version will be ++version, so 1
    let version = 0;

    // Uses initial tables
    version = createVersion(version); // v1

    // For some reason the next three are the same...
    tables = tables.concat(['address', 'category', 'comment', 'company', 'document', 'employment', 'event', 'provider', 'relation', 'resource']);
    version = createVersion(version); // v2
    version = createVersion(version); // v3
    version = createVersion(version); // v4

    tables = tables.concat(['referral']);
    version = createVersion(version); // v5

    tables = tables.concat(['campaign', 'campaignResponse', 'lead', 'opportunity', 'opportunityAction']);
    version = createVersion(version); // v6

    tables = tables.concat(['relationType']);
    version = createVersion(version); // v7

    tables = tables.concat(['identification']);
    version = createVersion(version); // v8

    // Remove campaignResponse, add others
    const index = tables.indexOf('campaignResponse');
    tables.splice(index, 1);
    tables = tables.concat(['caseType', 'conflict', 'milestone', 'question', 'answer', 'redFlagType', 'redFlag']);
    version = createVersion(version); // v9

    tables = tables.concat(['option']);
    version = createVersion(version); // v10

    tables = tables.concat(['activity']);
    version = createVersion(version); // v11

    tables = tables.concat(['activityCategory', 'activityType', 'matter', 'expense', 'transaction']);
    version = createVersion(version); // v12

    tables = tables.concat(['timer']);
    version = createVersion(version); // v13

    tables = tables.concat(['role', 'privilege', 'group']);
    version = createVersion(version); // v14

    tables = tables.concat(['appointmentEntry']);
    version = createVersion(version); // v15

    tables = tables.concat(['lookup']);
    version = createVersion(version); // v16

    tables = tables.concat(['bankAccount']);
    version = createVersion(version); // v17

    tables = tables.concat(['invoice', 'discount']);
    version = createVersion(version); // v18

    tables = tables.concat(['participant']);
    version = createVersion(version); // v19

    tables = tables.concat(['importExport']);
    version = createVersion(version); // v20

    tables = tables.concat(['importSource']);
    version = createVersion(version); // v21

    tables = tables.concat(['escrow']);
    version = createVersion(version); // v22

    // Remove action, opportunity, opportunityAction and timer tables
    const toRemoveV23 = ['action', 'opportunity', 'opportunityAction', 'timer'];
    toRemoveV23.forEach(table => {
        tables.splice(tables.indexOf(table), 1);
    });
    version = createVersion(version); // v23

    // Remove action, opportunity, opportunityAction and timer tables
    const toRemoveV24 = ['relation', 'relationType'];
    toRemoveV24.forEach(table => {
        tables.splice(tables.indexOf(table), 1);
    });
    version = createVersion(version); // v24

    tables = tables.concat(['flatFee']);
    version = createVersion(version); // v25

    tables = tables.concat(['flatFeeMilestone']);
    version = createVersion(version); // v26

    tables = tables.concat(['like']);
    version = createVersion(version); // v27

    tables = tables.concat(['templateTask']);
    version = createVersion(version); // v28

    tables = tables.concat(['blackout', 'docket', 'timeOff']);
    version = createVersion(version); // v29

    tables = tables.concat(['paymentMethod']);
    version = createVersion(version); // v30

    tables = tables.concat(['invoiceBatch', 'invoiceBatchItem']);
    version = createVersion(version); // v31

    // Remove action, opportunity, opportunityAction and timer tables
    const toRemoveV32 = [
        'appointmentEntry', 'company', 'employment', 'escrow', 'goal',
        'PaymentMethod', 'provider', 'testChild', 'testParent'
    ]; // v32

    toRemoveV32.forEach(table => {
        tables.splice(tables.indexOf(table), 1);
    });

    tables = tables.concat(['entityTimestamp']);
    version = createVersion(version); // v33

    // This version has no new tables, but we need to change the keys and values for anything already in the cahce,
    // as we no longer use underscores in variable names.
    version = createVersion(version); // v35 CHANGE THE CONSTANT WHEN YOU CHANGE THIS!

    // Drop the 'like' tables
    tables = tables.filter(table => table !== 'like');
    version = createVersion(version);

    // Add the new accounting entities
    tables = tables.concat(['accountingConnection']);
    version = createVersion(version);

    // v32 splice bugs accidentally removed docket, invoiceBatch, invoiceBatchItem, and timeOff
    // (caused by indexOf returning -1 for missing entries, making splice(-1,1) remove the last element).
    // Re-add those, plus entities that were never added to IDB.
    tables = tables.concat([
        'caseTypeStage', 'deadline', 'deadlineRule', 'docket',
        'invoiceBatch', 'invoiceBatchItem', 'matterStage', 'timeOff',
        'court', 'courtDeadlineRule', 'holidayCalendar'
    ]);
    version = createVersion(version); // CHANGE THE VERSION CONSTANT WHEN YOU CHANGE THIS!

    ls.setNumeric(ls.IDB_VERSION_CURRENT, version);

    // Reuse the instance captured above — defining schema versions never loses it.
    ls.setNumeric(ls.IDB_VERSION_RUNNING, db.verno);

    if (logging) console.debug(`⟴ IDB: open() completed — db="${db.name}", version=${db.verno}, tables=${db.tables.length} [${db.tables.map(t => t.name).join(', ')}]`);

    return Ok(db);
}

/**
 * Clear all tables (stores).  CAREFUL!
 * @returns {Promise<void>}
 */
export async function clearErrors(): Promise<Result<void, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: clearErrors() failed — no database instance. ${dbResult.unwrapErr().message}`));

    const db = dbResult.unwrap();
    console.warn(`⟴ IDB: clearErrors() clearing all tables in db="${db.name}" (${db.tables.length} tables)`);

    // Each table's clear() is individually .catch()'d, so the Promise.all never
    // rejects — no outer try/catch needed (it would be unreachable).
    await Promise.all(db.tables.map(table =>
        table.clear().catch(err =>
            console.warn(`⟴ IDB: clearErrors() failed to clear table "${table.name}" in db="${db.name}". ${errMsg(err)}`)
        )
    ));
    queueCountSlice.set('count', 0);
    return Ok(null);
}

/**
 * Get a list of all database stores.
 */
export async function getStoreNames(): Promise<IDBDatabaseInfo[]> {
    return await indexedDB.databases();
}

export type StoreResult = Record<string, EntityType[]>;

function getTable(store: string): Result<Table<any, any, any>, Error> {
    // Every caller verifies getDB() first, so the instance is cached here.
    const db = getDB().unwrap();
    const table = db.tables.find(t => t.name === store);
    if (!table) {
        const available = db.tables.length > 0 ? db.tables.map(t => t.name).join(', ') : '(none — idb.open() was never called or db was closed)';
        if (logging) console.debug(`⟴ IDB: getTable("${store}") — store not found. db="${db.name}", isOpen=${db.isOpen()}, tables=${db.tables.length} [${available}]`);
        return Err(new Error(`⟴ IDB: getTable("${store}") failed — store not found in db "${db.name}" (${db.tables.length} tables). Available: [${available}]`));
    }
    return Ok(table);
}

/**
 * Get all items from a single store.
 *
 * @param {string} storeName
 * @returns {Promise<any[]>}
 * @async
 */
export async function getSingleStore(storeName: string): Promise<Result<EntityType[], Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: getSingleStore("${storeName}") failed — no database instance. ${dbResult.unwrapErr().message}`));

    const table = getTable(storeName);
    if (table.isErr()) {
        return Err(table.unwrapErr());
    }

    try {
        const result = await table.unwrap().toArray();
        const casts = result.map(item => cast<EntityType>(storeName, item));
        return Ok(casts);
    }
    catch (err) {
        return Err(new Error(`⟴ IDB: getSingleStore("${storeName}") failed during toArray(). ${errMsg(err)}`));
    }
}

/**
 * This is called from Help, so we take errors and put them in the resulting object instead of throwing them.
 *
 * @async
 * @returns {Promise<Result<any[], Error>>}
 */
export async function getAll(): Promise<Result<StoreResult, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: getAll() failed — no database instance. ${dbResult.unwrapErr().message}`));

    // Get all stores
    const db = dbResult.unwrap();
    const result: StoreResult = {};

    for (let i = 0; i < db.tables.length; i++) {
        const name = db.tables[i].name;
        const storeResult = await getSingleStore(name);
        if (storeResult.isOk()) {
            const items = storeResult.unwrap();
            if (items.length > 0) result[name] = items;
        }
        else {
            console.warn(`⟴ IDB: getAll() skipping store "${name}" (${i + 1}/${db.tables.length}). ${storeResult.unwrapErr().message}`);
        }
    }

    return Ok(result);
}

/**
 * Reset the fatalError for all entries in the given store, or all stores if no store is given.
 *
 * @param {string} store
 */
export async function resetAll(store?: string): Promise<Result<boolean, Error>> {
    // No getDB() pre-check: getSingleStore()/getAll() below already verify it, so a
    // missing database surfaces through their Err (and an unknown `store` surfaces
    // as a read failure). Avoids a redundant, unreachable check.
    let items: StoreResult = {};

    if (store) {
        const values = await getSingleStore(store);
        if (values.isErr()) return Err(new Error(`⟴ IDB: resetAll("${store}") failed to read store. ${values.unwrapErr().message}`));
        else items[store] = values.unwrap();
    }
    else {
        const _items = await getAll();
        if (_items.isErr()) return Err(new Error(`⟴ IDB: resetAll() failed to read all stores. ${_items.unwrapErr().message}`));
        else items = _items.unwrap();
    }

    for (const storeName of Object.keys(items)) {
        // storeName came from getSingleStore()/getAll(), so the table exists.
        const t = getTable(storeName).unwrap();
        for (const item of items[storeName]) {
            item.fatalError = null;
            await t.put(item, item.addedOn);
        }
    }

    return Ok(true);
}

/**
 * Retrieve an item from a specific store by its index.
 *
 * @param {string} store - The name of the store to retrieve the item from.
 * @param {number} index - The index of the item to retrieve.
 * @returns {Promise<any>} - A promise that resolves to the item retrieved from the store.
 * @throws {Error} - Throws an error if the store or index is not provided.
 */
export async function get(store: string, index: number | null | undefined): Promise<Result<any, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: get("${store}", ${index}) failed — no database instance. ${dbResult.unwrapErr().message}`));

    if (store && index != null) {
        const table = getTable(store);
        if (table.isErr()) return Err(new Error(`⟴ IDB: get("${store}", ${index}) failed — could not access table. ${table.unwrapErr().message}`));

        return Ok(await table.unwrap().get(index));
    }
    else {
        return Err(new Error(`⟴ IDB: get() failed — missing required parameters (store=${JSON.stringify(store)}, index=${JSON.stringify(index)})`));
    }
}

/**
 * Add an item to the database.  Note that newly created items that have not been saved to the database yet
 * (status = 'C' and fresh = true) will NOT update their addedOn.  Since we haven't saved these to the database
 * yet, we should just replace the item in the database.
 *
 * @param {string} store
 * @param {object|[]} update
 * @returns {Promise<void>}
 * @throws {DexieError}
 */
export async function add(store: string, update: EntityType | EntityType[]): Promise<Result<void, Error>> {
    try {
        const dbResult = getDB();
        if (dbResult.isErr()) return Err(new Error(`⟴ IDB: add("${store}") failed — no database instance. ${dbResult.unwrapErr().message}`));

        const db = dbResult.unwrap();
        const items = Array.isArray(update) ? update : [update];

        // Throw an error if we get called without a store or an item
        if (!store) return Err(new Error(`⟴ IDB: add() failed — called without a store name`));
        if (!update) return Err(new Error(`⟴ IDB: add("${store}") failed — no update payload passed`));

        for (let i = 0; i < items.length; i++) {
            let item = items[i];

            if (logging) console.debug(`⟴ IDB: add(${store})`, item.addedOn, item.id);

            // If this is an old-style _addedOn, we need to cast it before adding it.
            if (Object.hasOwn(item, '_addedOn')) {
                item = cast(store, item);
            }

            // We cannot set an item without addedOn, as that is the key for this store
            if (!item.addedOn) return Err(new Error(`⟴ IDB: add("${store}") failed — item has no addedOn (id=${item.id}, status=${item.status}, fresh=${item.fresh})`));

            // If we pass the checks, put the item in the store.  put() either adds or replaces whatever is stored for the current addedOn
            // transaction() has already validated `store`, so getTable() can't fail inside it.
            await db.transaction('rw!', store, async () => {
                // If this item has not been stored to the database, we should get rid of any other instances of it
                if (item.fresh && item.status === 'C') {
                    const t = getTable(store).unwrap();
                    const matches = t.filter(o => (o.id === item.id) && o.fresh && (o.status === 'C'));
                    const count = await matches.count();
                    if (count > 0) {
                        if (logging) console.debug(`⟴ IDB: add("${store}") deleting ${count} duplicate fresh/C entries for id=${item.id}`);
                        await matches.delete();
                    }
                }

                // Now we can store the item
                if (logging) console.debug(`⟴ IDB: add("${store}") putting item id=${item.id}, addedOn=${item.addedOn}`);
                await getTable(store).unwrap().put(item, item.addedOn);
            });
        }
    }
    catch (err) {
        const itemCount = Array.isArray(update) ? update.length : 1;
        return Err(new Error(`⟴ IDB: add("${store}", ${itemCount} item(s)) failed. ${errMsg(err)}`));
    }

    const count = Array.isArray(update) ? update.length : 1;
    queueCountSlice.set('count', queueCountSlice.get('count') + count);
    return Ok(null);
}

/**
 * Remove an item from a specific store by its key.
 *
 * @param {string} store - The name of the store from which to remove the item.
 * @param {IndexableType} key - The key of the item to be removed.
 * @returns {Promise<void>} - A promise that resolves when the item is removed.
 * @throws {Error} - Throws an error if the item with the specified key is not found in the store.
 */
export async function remove(store: string, key: IndexableType | null | undefined): Promise<Result<void, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: remove("${store}", key=${key}) failed — no database instance. ${dbResult.unwrapErr().message}`));
    // OWA-554: a null/undefined key can't index any row — nothing to remove. Return
    // a no-op success (callers pass an entity's nullable addedOn; an unpersisted
    // entity has no cached row keyed by addedOn).
    if (key == null) return Ok(undefined);
    if (logging) console.debug(`⟴ IDB: remove("${store}", key=${key})`);

    const table = getTable(store);
    if (table.isErr()) return Err(new Error(`⟴ IDB: remove("${store}", key=${key}) failed — could not access table. ${table.unwrapErr().message}`));

    const t = table.unwrap();
    const item = await t.get(key);
    if (!item) {
        // Key not found - try to find and remove by matching the key as a different type (string vs number).
        // This handles stale IDB entries where the key type may have changed between versions.
        const altKey = typeof key === 'number' ? String(key) : Number(key);
        const altItem = isNaN(altKey as number) ? null : await t.get(altKey);
        if (altItem) {
            if (logging) console.debug(`⟴ IDB: remove("${store}", key=${key}) found via alternate key type ${typeof altKey}(${altKey})`);
            await t.delete(altKey);
            queueCountSlice.set('count', Math.max(0, queueCountSlice.get('count') - 1));
            return Ok(null);
        }

        console.warn(`⟴ IDB: remove("${store}", key=${key}) — key not found (tried original type ${typeof key} and alternate)`);
        return Ok(null);
    }

    if (logging) console.debug(`⟴ IDB: remove("${store}", key=${key}) — deleting`);
    await t.delete(key);

    queueCountSlice.set('count', Math.max(0, queueCountSlice.get('count') - 1));
    return Ok(null);
}

/**
 * Remove all IDB entries in a store matching an entity ID.
 * This is a fallback for when the primary key (addedOn) doesn't match due to key rewrites
 * in the Base constructor.
 *
 * @param {string} store - The name of the store (entity type)
 * @param {string} entityId - The entity ID to match against the store's `{store}Id` field
 * @returns {Promise<Result<number, Error>>} - The number of entries deleted
 */
export async function removeByEntityId(store: string, entityId: string): Promise<Result<number, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: removeByEntityId("${store}", entityId=${entityId}) failed — no database instance. ${dbResult.unwrapErr().message}`));

    const table = getTable(store);
    if (table.isErr()) return Err(new Error(`⟴ IDB: removeByEntityId("${store}", entityId=${entityId}) failed — could not access table. ${table.unwrapErr().message}`));

    const idKey = `${store}Id`;
    const count = await table.unwrap().where(idKey).equals(entityId).delete();
    if (count > 0) {
        if (logging) console.debug(`⟴ IDB: removeByEntityId(${store}, ${entityId}) removed ${count} entries`);
        queueCountSlice.set('count', Math.max(0, queueCountSlice.get('count') - count));
    }
    return Ok(count);
}

/**
 * Count all items across all IDB queue tables and set the queue count accordingly.
 * Call this after IDB is opened to ensure the count reflects any pre-existing entries.
 */
export async function initQueueCount(): Promise<void> {
    const dbResult = getDB();
    if (dbResult.isErr()) return;

    let total = 0;
    for (const table of dbResult.unwrap().tables) {
        try {
            total += await table.count();
        }
        catch {
            // Table may not exist yet — skip it
        }
    }

    if (logging) console.debug(`⟴ IDB: initQueueCount() = ${total}`);
    queueCountSlice.set('count', total);
}

/**
 *
 * @param {string} store - which store to use: this will be the entityType, e.g. 'user', 'task', 'contact'
 * @param {number} key - object's key, generally the addedOn, but could also be the id
 * @param {any} updates - an object with properties to updates, e.g. {firmId: '12343-...', fresh: false}
 * @returns {Promise<void>}
 */
export async function update(store: string, key: number, updates: any): Promise<Result<void, Error>> {
    const dbResult = getDB();
    if (dbResult.isErr()) return Err(new Error(`⟴ IDB: update("${store}", key=${key}) failed — no database instance. ${dbResult.unwrapErr().message}`));

    const table = getTable(store);
    if (table.isErr()) return Err(new Error(`⟴ IDB: update("${store}", key=${key}) failed — could not access table. ${table.unwrapErr().message}`));

    await table.unwrap().update(key || updates.id, updates);

    return Ok(null);
}
